/**
 * Integration health board (webhooks request; QM-D22-002 / QM-NC10-003) via /v1/integrations/health, plus the
 * connectors OAuth entry point: Connect -> /v1/connectors/oauth/start -> provider consent -> the portal worker's
 * /bff/oauth/connectors/callback -> /integrations/connect (ConnectCallbackPage) -> back here with ?connected=.
 */
import { useEffect, useState } from 'react';
import type { ConnectionFamily, ConnectionChannel } from '@qm/contracts/connection-groups';
import { ConnectionGroups } from './ConnectionGroups';
import { useSearchParams } from 'react-router';
import { Badge, Button, Heading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bff, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { ListScreen, str } from '../_shared/ListScreen';
import { connectErrorKey } from './ConnectCallbackPage';
import { AuditConnectBanner, auditConnectFrom } from './AuditConnectBanner';
import { YouTubePanel } from './YouTubeConnect';
import { BrandLogo } from '../../../shell/BrandLogo';
import { GA4_FLAG } from '@qm/contracts/connectors-ga4';
import { useFlags } from '../../../lib/flags';
import './integrations-studio.css';

/** Providers a customer can connect with OAuth from this screen (Bing is agency / later). */
export const CONNECTABLE = ['meta', 'google_ads', 'linkedin', 'tiktok', 'snap', 'gbp', 'google_search_console', 'ga4'] as const;
/** Providers listed only while their server-evaluated flag is on (the API refuses them otherwise). */
export const FLAGGED_PROVIDERS: Readonly<Partial<Record<(typeof CONNECTABLE)[number], string>>> = { ga4: GA4_FLAG };

/** Counts only verified connector results; unavailable endpoints never appear as zero. */
export function confirmedConnectionCount(
  connections: { provider: string; status: string }[] | null,
  social: { provider: string; status: string }[] | null,
): number | null {
  if (!connections || !social) return null;
  return new Set([...connections, ...social].filter((item) => item.status === 'connected').map((item) => item.provider)).size;
}

const studioText = {
  en: {
    kicker: 'Your growth ecosystem',
    hero: 'Everything connected. One clear workspace.',
    intro: 'Bring your marketing channels together, decide exactly what to share, and see connection health in one place.',
    steps: ['Connect securely', 'Choose assets', 'Grow with clarity'],
    metric: 'Connected services',
    metricNote: 'Live connections from your linked business accounts',
    waiting: 'Checking connections',
    providers: 'Connect your platforms',
    providerIntro: 'Start with Meta or Google, then add any specialist channels you use.',
    secure: 'Your accounts stay under your control',
    other: 'More channels',
    whatsapp: 'WhatsApp Business',
    whatsappNote: 'Connect your number and manage the messaging setup for this workspace.',
  },
  ar: {
    kicker: 'منظومة نمو أعمالك',
    hero: 'كل قنواتك متصلة. مساحة عمل واحدة وواضحة.',
    intro: 'اجمع قنوات التسويق وحدّد الأصول التي تريد مشاركتها وتابع حالة الاتصال من مكان واحد.',
    steps: ['اتصال آمن', 'اختيار الأصول', 'نمو بوضوح'],
    metric: 'الخدمات المتصلة',
    metricNote: 'اتصالات مؤكدة من حسابات أعمالك المرتبطة',
    waiting: 'جارٍ فحص الاتصالات',
    providers: 'اربط منصاتك',
    providerIntro: 'ابدأ بمنصتي Meta أو Google ثم أضف القنوات الأخرى التي تستخدمها.',
    secure: 'حساباتك تبقى تحت سيطرتك',
    other: 'قنوات إضافية',
    whatsapp: 'واتساب للأعمال',
    whatsappNote: 'اربط رقم العمل وأدر إعدادات المراسلة لمساحة العمل هذه.',
  },
} as const;

/** Only ever navigate to an https provider consent page returned by the api. */
export function safeAuthorizeUrl(u: unknown): string | null {
  if (typeof u !== 'string') return null;
  try { return new URL(u).protocol === 'https:' ? u : null; } catch { return null; }
}

export function IntegrationsPage() {
  const { t, locale, me } = useApp();
  const f = useFormat();
  const flag = useFlags();
  const copy = studioText[locale];
  const [params] = useSearchParams();
  const [queued, setQueued] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const connected = params.get('connected');
  const auditConnect = auditConnectFrom(params);
  const [connections,setConnections]=useState<{provider:string;status:string}[]|null>(null);
  const [social,setSocial]=useState<{provider:string;status:string}[]|null>(null);
  useEffect(()=>{
    let active=true;setConnections(null);setSocial(null);if(!me?.tenantId)return;
    const headers={'x-tenant-id':me.tenantId};
    void bff<{provider:string;status:string}[]>('/v1/connectors/connections',{headers}).then(r=>{if(active)setConnections(r);},()=>undefined);
    void bff<{provider:string;status:string}[]>('/v1/social/accounts',{headers}).then(r=>{if(active)setSocial(r);},()=>undefined);
    return()=>{active=false;};
  },[me?.tenantId]);
  const connectGroup=async(family:ConnectionFamily,channels:ConnectionChannel[])=>{
    if(busy||!me?.tenantId)return;setStartError(null);setBusy(family);
    try{const r=await bff<{authorizeUrl:string}>('/v1/connectors/oauth/group/start',{method:'POST',headers:{'x-tenant-id':me.tenantId},body:JSON.stringify({family,channels,returnTo:'/integrations'})});const url=safeAuthorizeUrl(r.authorizeUrl);if(!url)throw Error('Invalid URL');window.location.assign(url);}
    catch(e){setStartError(connectErrorKey(e instanceof BffError?(e.envelope?.error.details as {code?:string}|undefined)?.code:null));setBusy(null);}
  };
  const check = async () => { await bffJson('/v1/integrations/health/check', 'POST').then(() => setQueued(true), () => undefined); };
  const connect = async (provider: string) => {
    setStartError(null); setBusy(provider);
    try {
      const r = await bffJson<{ authorizeUrl: string }>('/v1/connectors/oauth/start', 'POST', { provider });
      const url = safeAuthorizeUrl(r.authorizeUrl);
      if (!url) throw new Error('bad authorize url');
      window.location.assign(url);
    } catch (e) {
      setStartError(connectErrorKey(e instanceof BffError ? (e.envelope?.error.details as { code?: string } | undefined)?.code : null));
      setBusy(null);
    }
  };
  const health = (r: Record<string, unknown>) => (r.health ?? {}) as { state?: string; action?: string; lastSuccessAt?: string | null; lastCheckedAt?: string | null };
  const confirmed = confirmedConnectionCount(connections, social);
  return (
    <div className="cu-integrations">
    <ListScreen titleKey="integrations.board.title" introKey="integrations.board.subtitle" path="/v1/integrations/health" emptyKey="integrations.board.empty"
      actions={<Button onClick={() => void check()}>{t('integrations.board.checkNow')}</Button>}
      columns={[
        { key: 'provider', header: t('shell.col.platform'), render: (r) => <span className="cu-integrations__platform"><BrandLogo provider={str(r.provider)} /><bdi>{f.text(r.displayName ?? r.provider)}</bdi></span> },
        { key: 'state', header: t('shell.col.status'), render: (r) => <Badge tone={health(r).action && health(r).action !== 'none' ? 'error' : 'neutral'}>{t(`integrations.health.state.${str(health(r).state ?? 'unknown')}`)}</Badge> },
        { key: 'action', header: t('shell.col.action'), render: (r) => t(`integrations.health.action.${str(health(r).action ?? 'none')}`) },
        { key: 'lastSuccessAt', header: t('integrations.board.lastSuccess'), render: (r) => (health(r).lastSuccessAt ? f.dateTime(health(r).lastSuccessAt) : t('integrations.board.never')) },
      ]}>
      <section className="cu-integrations__hero" aria-labelledby="integrations-studio-title" data-testid="integrations-studio">
        <div className="cu-integrations__hero-copy">
          <p className="cu-integrations__eyebrow">{copy.kicker}</p>
          <h2 id="integrations-studio-title">{copy.hero}</h2>
          <p>{copy.intro}</p>
          <ol className="cu-integrations__steps">
            {copy.steps.map((step, index) => <li key={step}><b aria-hidden="true">{index + 1}</b>{step}</li>)}
          </ol>
        </div>
        <div className="cu-integrations__meter" aria-live="polite">
          <p className="cu-integrations__meter-label">{copy.metric}</p>
          <strong className="cu-integrations__meter-num" data-testid="integrations-confirmed-count">{confirmed === null ? '—' : f.number(confirmed)}</strong>
          <div className="cu-integrations__meter-rule" aria-hidden="true"><span style={{ inlineSize: confirmed === null ? '0%' : `${Math.min(100, confirmed * 12.5)}%` }} /></div>
          <p className="cu-integrations__meter-note">{confirmed === null ? copy.waiting : copy.metricNote}</p>
        </div>
      </section>
      {auditConnect ? <AuditConnectBanner audit={auditConnect} /> : null}
      {queued ? <div className="cu-integrations__status"><Text role="status">{t('integrations.board.checkQueued')}</Text></div> : null}
      {connected && (CONNECTABLE as readonly string[]).includes(connected) ? (
        <div className="cu-integrations__status"><Text role="status">{t(params.get('reconnected') ? 'connectors.callback.reconnected' : 'connectors.callback.success', { provider: t(`connectors.provider.${connected}`) })}</Text></div>
      ) : null}
      <section className="cu-integrations__section" aria-labelledby="connect-title">
        <div className="cu-integrations__section-head">
          <div>
            <Heading level={2} id="connect-title">{copy.providers}</Heading>
            <Text tone="muted">{copy.providerIntro}</Text>
          </div>
          <p className="cu-integrations__assurance">{copy.secure}</p>
        </div>
        <Text tone="muted">{t('connectors.connect.scopes_note')}</Text>
        {startError ? <Text role="alert">{t(startError)}</Text> : null}
        <ConnectionGroups locale={locale} busy={busy!==null||!me?.tenantId} connections={connections} social={social} onConnect={(family,channels)=>void connectGroup(family,channels)}/>
      </section>
      <section className="cu-integrations__section" aria-labelledby="other-channels-title">
        <div className="cu-integrations__section-head">
          <Heading level={2} id="other-channels-title">{copy.other}</Heading>
        </div>
        <ul className="cu-integrations__provider-list">
          {CONNECTABLE.filter(p=>!['meta','google_ads','gbp','google_search_console'].includes(p) && (!FLAGGED_PROVIDERS[p] || flag(FLAGGED_PROVIDERS[p]!) === 'on')).map((p) => (
            <li className="cu-integrations__provider" key={p}>
              <div className="cu-integrations__provider-name">
                <BrandLogo provider={p} size={40} />
                <span>{t(`connectors.provider.${p}`)}</span>
              </div>
              <Button onClick={() => void connect(p)} disabled={busy !== null} aria-label={`${t('connectors.connect.button')} ${t(`connectors.provider.${p}`)}`}>
                {t('connectors.connect.button')}
              </Button>
            </li>
          ))}
        </ul>
      </section>
      {params.get('youtube') === 'connected' || params.get('youtube') === 'reconnected' ? (
        <div className="cu-integrations__status"><Text role="status">{t(`social.youtube.${params.get('youtube')}`)}</Text></div>
      ) : null}
      <YouTubePanel />
      <section className="cu-integrations__wa" aria-labelledby="wa-connect-title">
        <BrandLogo provider="whatsapp" size={50} />
        <div className="cu-integrations__wa-copy">
          <h3 id="wa-connect-title">{copy.whatsapp}</h3>
          <p>{copy.whatsappNote}</p>
        </div>
        {/* Full navigation is required for the WhatsApp connection's dedicated security headers. */}
        <a className="qm-button" href="/whatsapp/connect" data-variant="accent">{t('whatsapp-connect.integrations.link')}</a>
      </section>
    </ListScreen>
    </div>
  );
}
